import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Card from '../../../Card/Card';
import InputGroup from '../../../FormElements/InputGroup/InputGroup';
import SelectDropDown from '../../../FormElements/SelectDropDown/SelectDropDown';
import TabActionButtons from '../../../FormElements/TabActionButtons/TabActionButtons';
import ToggleSwitch from '../../../FormElements/ToggleSwitch/ToggleSwitch';
import './TabDropdown.css';

const SearchPropertyResultSettings = ({ onSave, onReset, settingsData, onSettingsChange, showToast }) => {
  const [settings, setSettings] = useState({
    hvnly_hideTopSearchTitle: false,
    hvnly_hideTopSearchBar: false,
    hvnly_hideLeftSearchBar: false,
    hvnly_numberOfColumns: '3',
    hvnly_searchResultPerpage: '6',
    hvnly_sidebarLayoutType: 'LEFT',
  });

  useEffect(() => {
    if (settingsData && typeof settingsData === 'object' && Object.keys(settingsData).length > 0) {
      const searchResultFields = {
        hvnly_hideTopSearchTitle: settingsData.hvnly_hideTopSearchTitle !== undefined ? settingsData.hvnly_hideTopSearchTitle : settings.hvnly_hideTopSearchTitle,
        hvnly_hideTopSearchBar: settingsData.hvnly_hideTopSearchBar !== undefined ? settingsData.hvnly_hideTopSearchBar : settings.hvnly_hideTopSearchBar,
        hvnly_hideLeftSearchBar: settingsData.hvnly_hideLeftSearchBar !== undefined ? settingsData.hvnly_hideLeftSearchBar : settings.hvnly_hideLeftSearchBar,
        hvnly_numberOfColumns: settingsData.hvnly_numberOfColumns !== undefined ? settingsData.hvnly_numberOfColumns : settings.hvnly_numberOfColumns,
        hvnly_searchResultPerpage: settingsData.hvnly_searchResultPerpage !== undefined ? settingsData.hvnly_searchResultPerpage : settings.hvnly_searchResultPerpage,
        hvnly_sidebarLayoutType: settingsData.hvnly_sidebarLayoutType !== undefined ? settingsData.hvnly_sidebarLayoutType : settings.hvnly_sidebarLayoutType,
      };
      setSettings(prev => ({ ...prev, ...searchResultFields }));
    }
  }, [settingsData]);

  const handleInputChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(settings);
      return true;
    }
    return false;
  };

  const handleReset = async () => {
    const defaultSettings = {
      hvnly_hideTopSearchTitle: false,
      hvnly_hideTopSearchBar: false,
      hvnly_hideLeftSearchBar: false,
      hvnly_numberOfColumns: '3',
      hvnly_searchResultPerpage: '6',
      hvnly_sidebarLayoutType: 'LEFT',
    };
    setSettings(defaultSettings);
    if (onSettingsChange) {
      onSettingsChange(defaultSettings);
    }
    if (onReset) {
      await onReset(defaultSettings);
      return true;
    }
    return false;
  };

  const sidebarLayoutOptions = [
    { value: 'LEFT', label: __('Left Sidebar Filter', 'havenlytics') },
    { value: 'RIGHT', label: __('Right Sidebar Filter', 'havenlytics') },
  ];

  const numberOfColumnsOptions = [
    { value: '1', label: __('1 Column', 'havenlytics') },
    { value: '2', label: __('2 Columns', 'havenlytics') },
    { value: '3', label: __('3 Columns', 'havenlytics') },
    { value: '4', label: __('4 Columns', 'havenlytics') },
  ];

  return (
    <div 
      className="hvnly-settings-api__content hvnly-settings-api__content--active" 
      id="search-result"
    >
      <h2>{ __('Layout & Search Result Page', 'havenlytics') }</h2>

      {/* Hide Top Search Page Title Card */}
      <Card title={__("Hide Top Search Page Title", "havenlytics")} icon="fas fa-heading" collapsible>
        <ToggleSwitch
          id="hvnly_hideTopSearchTitle"
          checked={settings.hvnly_hideTopSearchTitle}
          onChange={e => handleInputChange('hvnly_hideTopSearchTitle', e.target.checked)}
          label={__("Hide Top Search Title", "havenlytics")}
        />
      </Card>

      {/* Hide Top Search Bar Card */}
      <Card title={__("Hide Top Search Bar", "havenlytics")} icon="fas fa-search" collapsible>
        <ToggleSwitch
          id="hvnly_hideTopSearchBar"
          checked={settings.hvnly_hideTopSearchBar}
          onChange={e => handleInputChange('hvnly_hideTopSearchBar', e.target.checked)}
          label={__("Hide Top Search Bar", "havenlytics")}
        />
      </Card>

      {/* Hide Left Search Filter Sidebar Card */}
      <Card title={__("Hide Left Search Filter Sidebar", "havenlytics")} icon="fas fa-filter" collapsible>
        <ToggleSwitch
          id="hvnly_hideLeftSearchBar"
          checked={settings.hvnly_hideLeftSearchBar}
          onChange={e => handleInputChange('hvnly_hideLeftSearchBar', e.target.checked)}
          label={__("Hide Left Search Filter Sidebar", "havenlytics")}
        />
      </Card>
      
      {/* Search Property Layout Card */}
      <Card title={__("Search Property Layout", "havenlytics")} icon="fas fa-th-large" collapsible>
        <SelectDropDown
          label={__("Select Sidebar Layout", "havenlytics")}
          id="hvnly_sidebarLayoutType"
          value={settings.hvnly_sidebarLayoutType}
          options={sidebarLayoutOptions}
          onChange={(value) => handleInputChange('hvnly_sidebarLayoutType', value)}
        />
  
        <SelectDropDown
          label={__("Number of Columns", "havenlytics")}
          id="hvnly_numberOfColumns"
          value={settings.hvnly_numberOfColumns}
          options={numberOfColumnsOptions}
          onChange={(value) => handleInputChange("hvnly_numberOfColumns", value)}
        />
        
        <InputGroup
          label={__("Search Results Per Page", "havenlytics")}
          id="hvnly_searchResultPerpage"
          type="number"
          value={settings.hvnly_searchResultPerpage}
          onChange={(value) => handleInputChange("hvnly_searchResultPerpage", value)}
        />
      </Card>

      <TabActionButtons
        onSave={handleSave}
        onReset={handleReset}
        showToast={showToast}
        saveLabel={__("Save Search Result Settings", "havenlytics")}
        resetLabel={__("Reset Changes", "havenlytics")}
      />
    </div>
  );
};

export default SearchPropertyResultSettings;